import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Icons,
  iconSize,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  FormHelperText,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  FileDetail,
  MandateSource,
  ProceedingsOutcome,
  Step9Data,
} from '@munifocus/contracts-gov';
import { EscalationStepLayout } from '../../components/EscalationStepLayout';
import { useSaveStage } from '../../lib/files-api';
import { useSubmitValidation } from '../../lib/useSubmitValidation';
import {
  useFileProceedings,
  useProceedingsForFile,
  useResolveProceedings,
} from '../../lib/proceedings-api';

/**
 * Type 3 sub-flow, Step 9 — Proceedings in Provincial Court (ticket E6 + Epic P).
 * The officer files penal proceedings under a council mandate (s. 137) or a
 * standing delegation (s. 72 / s. 80). Filing hands off to the Provincial
 * Offences Procedure Module (Epic P): it persists a proceedings record and the
 * matching court docket entry via the API. Resolved closes the file; unresolved
 * returns it to Step 7 for re-deliberation.
 */
export function Step9Proceedings({ file }: { file: FileDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveStage(file.id, 'step_9');
  const initial = (file.stageData?.step_9 ?? {}) as Step9Data;

  const { data: proceedings = [] } = useProceedingsForFile(file.id);
  const fileProceedings = useFileProceedings(file.id);
  const resolveProceedings = useResolveProceedings(file.id);

  const [mandateSource, setMandateSource] = useState<MandateSource | ''>(
    initial.mandateSource ?? '',
  );
  const [courtFileNumber, setCourtFileNumber] = useState('');
  const [clerkCountersigned, setClerkCountersigned] = useState(false);
  const [outcome, setOutcome] = useState<ProceedingsOutcome | ''>(initial.outcome ?? '');

  // The matter awaiting the court's outcome, if any. "Filed" means one is open:
  // once every matter has an outcome (a file returned to council after an
  // unresolved one), the step offers filing again rather than hiding the form.
  const open = proceedings.find((p) => p.status === 'filed');
  const filed = Boolean(open);
  const everFiled = proceedings.length > 0;
  // Once filed, the matter carries its own mandate; before that, the officer's
  // selection drives both the filing and the saved escalation record.
  const effectiveMandate = (mandateSource || open?.mandateSource || '') as MandateSource | '';
  const hasMandate = Boolean(mandateSource) || Boolean(open?.mandateSource);
  const resolved = filed && outcome === 'resolved';
  const mandateMessage = hasMandate ? null : t('gov.proceedings.selectMandateFirst');
  const v = useSubmitValidation({ mandate: mandateMessage });
  // Filing checks its own pair: the mandate it is filed under, and the court's number.
  const vFile = useSubmitValidation({
    mandate: mandateMessage,
    courtFileNumber: courtFileNumber.trim() ? null : t('common.validation.required'),
  });
  const mandateError = v.errors.mandate ?? vFile.errors.mandate;

  const onFile = () =>
    fileProceedings.mutate({
      mandateSource: effectiveMandate as MandateSource,
      courtFileNumber: courtFileNumber.trim(),
      clerkCountersigned,
    });

  const saveStep = () =>
    save.mutate(
      {
        mandateSource: effectiveMandate || undefined,
        filed: everFiled,
        outcome: filed ? outcome || undefined : undefined,
        // A resolved matter is terminal — advance escalation -> closed.
        advance: resolved,
      },
      { onSuccess: () => navigate(`/files/${file.id}`) },
    );

  // The outcome belongs to the matter first: record it on the proceeding (which
  // takes it off the open docket), then on the step.
  const onSave = () => {
    if (open && outcome) {
      resolveProceedings.mutate({ id: open.id, outcome }, { onSuccess: saveStep });
    } else {
      saveStep();
    }
  };

  return (
    <EscalationStepLayout
      file={file}
      step="step_9"
      onSave={v.guard(onSave)}
      saving={save.isPending || resolveProceedings.isPending}
      saveLabel={resolved ? t('gov.escalation.step6.closeFile') : t('common.actions.save')}
    >
      {save.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}

      <FormControl fullWidth size="small" error={Boolean(mandateError)}>
        <InputLabel>{t('gov.escalation.step9.mandate')}</InputLabel>
        <Select
          label={t('gov.escalation.step9.mandate')}
          aria-label={t('gov.escalation.step9.mandate')}
          inputRef={(el: { focus: () => void } | null) => {
            v.register('mandate')(el);
            vFile.register('mandate')(el);
          }}
          value={effectiveMandate}
          disabled={filed}
          onChange={(e) => setMandateSource(e.target.value as MandateSource)}
        >
          <MenuItem value="council_resolution">{t('gov.escalation.step9.mandateCouncil')}</MenuItem>
          <MenuItem value="standing_delegation">
            {t('gov.escalation.step9.mandateDelegation')}
          </MenuItem>
        </Select>
        {mandateError && <FormHelperText>{mandateError}</FormHelperText>}
      </FormControl>

      <Divider />

      <Box>
        <Typography variant="subtitle2">{t('gov.proceedings.moduleTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('gov.proceedings.moduleIntro')}
        </Typography>
      </Box>

      {!filed && (
        <Stack spacing={2}>
          {everFiled && <Alert severity="info">{t('gov.proceedings.refileIntro')}</Alert>}
          <TextField
            fullWidth
            size="small"
            label={t('gov.proceedings.courtFileNumber')}
            {...vFile.field('courtFileNumber')}
            value={courtFileNumber}
            onChange={(e) => setCourtFileNumber(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={clerkCountersigned}
                onChange={(e) => setClerkCountersigned(e.target.checked)}
              />
            }
            label={t('gov.proceedings.clerkCountersigned')}
          />
          <Box>
            <Button
              variant="contained"
              startIcon={<Icons.Gavel />}
              onClick={vFile.guard(onFile)}
              disabled={fileProceedings.isPending}
            >
              {fileProceedings.isPending
                ? t('gov.proceedings.filing')
                : t('gov.proceedings.fileAction')}
            </Button>
          </Box>
        </Stack>
      )}

      {filed && open && (
        <Alert severity="success" icon={<Icons.Gavel />}>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              {t('gov.proceedings.filedSummary', { courtFileNumber: open.courtFileNumber })}
            </Typography>
            <Button
              variant="text"
              size="small"
              endIcon={<Icons.ChevronRight size={iconSize.md} />}
              onClick={() => navigate('/docket')}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('gov.proceedings.viewDocket')}
            </Button>
          </Stack>
        </Alert>
      )}

      {filed && (
        <>
          <Divider />
          <Typography variant="subtitle2">{t('gov.escalation.step9.outcome')}</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>{t('gov.escalation.step9.outcome')}</InputLabel>
            <Select
              label={t('gov.escalation.step9.outcome')}
              aria-label={t('gov.escalation.step9.outcome')}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as ProceedingsOutcome)}
            >
              <MenuItem value="resolved">{t('gov.escalation.step9.resolved')}</MenuItem>
              <MenuItem value="unresolved">{t('gov.escalation.step9.unresolved')}</MenuItem>
            </Select>
          </FormControl>
          {resolved && <Alert severity="warning">{t('gov.escalation.step9.resolvedNote')}</Alert>}
          {outcome === 'unresolved' && (
            <Alert severity="info">{t('gov.escalation.step9.unresolvedNote')}</Alert>
          )}
        </>
      )}
    </EscalationStepLayout>
  );
}
